import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PLANS = [
  {
    id: 'legacy',
    name: 'Family Legacy',
    price: '$89',
    period: '/year',
    description: 'Everything your family needs to build a lasting Bible legacy',
    features: ['Up to 6 family members', 'Unlimited recordings', 'Bible listening plans', 'Study notes', 'Family sharing'],
    featured: true,
  },
  {
    id: 'plus',
    name: 'Family+',
    price: '$149',
    period: '/year',
    description: 'For larger families and those who want the full experience',
    features: ['Everything in Family Legacy', 'Unlimited family members', 'Priority support', 'Early access to new features'],
    featured: false,
  },
];

const FUNCTIONS_BASE = 'https://us-central1-familybibleapp.cloudfunctions.net';

export default function GiftPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const giftId = searchParams.get('giftId');

  const [step, setStep] = useState(1); // 1=plan, 2=recipient, 3=giver
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [giverName, setGiverName] = useState('');
  const [giverEmail, setGiverEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Handle Stripe redirect back
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-space-cadet mb-2">Gift Sent!</h1>
          <p className="text-gray-500 mb-6">
            Your gift is on its way. The recipient will receive an email with a link to redeem their 1-year subscription.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-space-cadet mb-2">Payment Cancelled</h1>
          <p className="text-gray-500 mb-6">No worries — your gift wasn't charged. You can try again anytime.</p>
          <button
            onClick={() => { window.history.replaceState({}, '', '/gift'); setStep(1); }}
            className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/createGiftCheckout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: selectedPlan.id,
          recipientName,
          recipientEmail,
          message,
          giverName,
          giverEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const stepLabel = ['', 'Choose a Plan', 'Who is it for?', 'Your Details'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 pb-16">
      <div className="max-w-2xl mx-auto px-4 pt-8">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white">Give the Gift of Legacy</h1>
          <p className="text-white/70 mt-2">Give someone a year of recording their family's Bible story.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all ${
                s < step ? 'bg-cerulean text-white' : s === step ? 'bg-white text-space-cadet' : 'bg-white/20 text-white/50'
              }`}>
                {s < step ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : s}
              </div>
              {s < 3 && <div className={`flex-1 max-w-12 h-0.5 ${s < step ? 'bg-cerulean' : 'bg-white/20'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-space-cadet/5 px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-space-cadet">Step {step} of 3 — {stepLabel[step]}</h2>
          </div>

          <div className="p-6">

            {/* Step 1: Choose Plan */}
            {step === 1 && (
              <div className="space-y-4">
                {PLANS.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan)}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                      selectedPlan?.id === plan.id
                        ? 'border-cerulean bg-cerulean/5'
                        : 'border-gray-200 hover:border-cerulean/40'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-space-cadet text-lg">{plan.name}</span>
                          {plan.featured && (
                            <span className="text-xs bg-cerulean text-white px-2 py-0.5 rounded-full font-semibold">Most Popular</span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm mt-1">{plan.description}</p>
                        <ul className="mt-3 space-y-1">
                          {plan.features.map(f => (
                            <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                              <svg className="w-4 h-4 text-cerulean flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-2xl font-bold text-cerulean">{plan.price}</span>
                        <p className="text-xs text-gray-400">{plan.period}</p>
                      </div>
                    </div>
                    {selectedPlan?.id === plan.id && (
                      <div className="mt-3 flex items-center gap-2 text-cerulean text-sm font-semibold">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Selected
                      </div>
                    )}
                  </button>
                ))}

                <button
                  onClick={() => setStep(2)}
                  disabled={!selectedPlan}
                  className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed mt-2"
                >
                  Continue →
                </button>
              </div>
            )}

            {/* Step 2: Recipient Details */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="p-3 bg-cerulean/5 rounded-xl border border-cerulean/15 flex items-center gap-3">
                  <svg className="w-5 h-5 text-cerulean flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                  <span className="text-sm text-space-cadet font-medium">
                    Gifting: {selectedPlan?.name} — {selectedPlan?.price}/year
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient's Name</label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={e => setRecipientName(e.target.value)}
                    placeholder="Grandma Rose"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient's Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={e => setRecipientEmail(e.target.value)}
                    placeholder="grandma@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">We'll send their redemption link here.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message <span className="text-gray-400">(optional)</span></label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="I want our family to always hear your voice reading the Bible..."
                    rows={3}
                    maxLength={300}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right">{message.length}/300</p>
                </div>

                <div className="flex gap-3 mt-2">
                  <button
                    onClick={() => setStep(1)}
                    className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!recipientEmail.includes('@')}
                    className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Giver Details */}
            {step === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">We'll send you a receipt and let the recipient know who the gift is from.</p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                  <input
                    type="text"
                    value={giverName}
                    onChange={e => setGiverName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={giverEmail}
                    onChange={e => setGiverEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cerulean focus:border-transparent outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Your Stripe receipt goes here.</p>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gift</span>
                    <span className="font-medium">{selectedPlan?.name} — 1 year</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Recipient</span>
                    <span className="font-medium">{recipientName || recipientEmail}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                    <span className="font-semibold text-gray-700">Total</span>
                    <span className="font-bold text-cerulean">{selectedPlan?.price}</span>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleCheckout}
                    disabled={loading || !giverEmail.includes('@')}
                    className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Processing…</>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Pay {selectedPlan?.price} Securely
                      </>
                    )}
                  </button>
                </div>

                <p className="text-center text-xs text-gray-400">
                  Powered by Stripe · Secure payment
                </p>
              </div>
            )}

          </div>
        </div>

        <p className="text-center text-white/50 text-xs mt-6">
          Questions? <a href="mailto:hello@familybibleapp.com" className="underline hover:text-white/80">hello@familybibleapp.com</a>
        </p>
      </div>
    </div>
  );
}
